import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Music2, Users, X, Play, Check, XCircle, Clock, UserPlus, SkipBack, SkipForward, Shuffle, Pause } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const PLATFORM_COLORS = {
  youtube: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", label: "YouTube" },
};

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function PlaylistDetailPage() {
  const { api, user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", artist: "", url: "" });
  const [adding, setAdding] = useState(false);
  const [activeTrack, setActiveTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shuffleMode, setShuffle] = useState(false);
  const [shuffledOrder, setShuffledOrder] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const iframeRef = useRef(null);
  const autoNextTimer = useRef(null);

  const fetchPlaylist = useCallback(async () => {
    try {
      const r = await api.get(`/playlists/${id}`);
      setPlaylist(r.data.playlist);
    } catch {
      navigate("/playlists");
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => { fetchPlaylist(); }, [fetchPlaylist]);

  // Generate shuffled order when tracks or shuffle mode changes
  useEffect(() => {
    if (playlist?.tracks?.length) {
      setShuffledOrder(shuffleArray(playlist.tracks.map((_, i) => i)));
    }
  }, [playlist?.tracks?.length, shuffleMode]);

  // Listen for YouTube embed messages to detect track end
  useEffect(() => {
    const handleMessage = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        // YouTube: video ended (state 0)
        if (data?.event === "onStateChange" && data?.info === 0) {
          handleNext();
        }
        // YouTube iframe API: infoDelivery
        if (data?.event === "infoDelivery" && data?.info?.playerState === 0) {
          handleNext();
        }
      } catch (err) {
        console.error("YouTube message parse error:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  // YouTube uses proper event detection, no fallback timer needed
  useEffect(() => {
    clearTimeout(autoNextTimer.current);
    return () => clearTimeout(autoNextTimer.current);
  }, [activeTrack?.id, isPlaying]);

  const getPlayableTracks = () => (playlist?.tracks || []).filter((t) => t.embed_url);

  const getCurrentIndex = () => {
    const tracks = getPlayableTracks();
    return tracks.findIndex((t) => t.id === activeTrack?.id);
  };

  const getNextTrackIndex = (currentIdx, tracks) => {
    if (shuffleMode) {
      const shuffled = shuffledOrder.filter((i) => i < tracks.length);
      const currentShufflePos = shuffled.indexOf(currentIdx);
      const nextPos = (currentShufflePos + 1) % shuffled.length;
      return shuffled[nextPos];
    }
    return (currentIdx + 1) % tracks.length;
  };

  const getPrevTrackIndex = (currentIdx, tracks) => {
    if (shuffleMode) {
      const shuffled = shuffledOrder.filter((i) => i < tracks.length);
      const currentShufflePos = shuffled.indexOf(currentIdx);
      const prevPos = (currentShufflePos - 1 + shuffled.length) % shuffled.length;
      return shuffled[prevPos];
    }
    return (currentIdx - 1 + tracks.length) % tracks.length;
  };

  const playTrack = (track) => {
    if (track.embed_url) {
      setActiveTrack(track);
      setIsPlaying(true);
    } else {
      window.open(track.url, "_blank");
    }
  };

  const handleNext = () => {
    const tracks = getPlayableTracks();
    if (tracks.length === 0) return;
    const idx = getCurrentIndex();
    const nextIdx = getNextTrackIndex(idx, tracks);
    setActiveTrack(tracks[nextIdx]);
    setIsPlaying(true);
  };

  const handlePrev = () => {
    const tracks = getPlayableTracks();
    if (tracks.length === 0) return;
    const idx = getCurrentIndex();
    const prevIdx = getPrevTrackIndex(idx, tracks);
    setActiveTrack(tracks[prevIdx]);
    setIsPlaying(true);
  };

  const toggleShuffle = () => {
    setShuffle((s) => {
      if (!s) setShuffledOrder(shuffleArray((playlist?.tracks || []).map((_, i) => i)));
      return !s;
    });
  };

  const stopPlayback = () => {
    setActiveTrack(null);
    setIsPlaying(false);
    clearTimeout(autoNextTimer.current);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) return;
    setAdding(true);
    try {
      const res = await api.post(`/playlists/${id}/tracks`, form);
      if (res.data.status === "approved") {
        setPlaylist((prev) => ({ ...prev, tracks: [...(prev.tracks || []), res.data.track] }));
      }
      setShowAdd(false);
      setForm({ title: "", artist: "", url: "" });
      if (res.data.status === "pending") {
        toast.success("Ta proposition a ete envoyee au createur de la playlist !");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setAdding(false);
  };

  const handleReview = async (trackId, action) => {
    try {
      const res = await api.post(`/playlists/${id}/tracks/${trackId}/review`, { action });
      if (action === "approve" && res.data.track) {
        setPlaylist((prev) => ({
          ...prev,
          tracks: [...(prev.tracks || []), res.data.track],
          pending_tracks: (prev.pending_tracks || []).filter((t) => t.id !== trackId),
        }));
      } else {
        setPlaylist((prev) => ({
          ...prev,
          pending_tracks: (prev.pending_tracks || []).filter((t) => t.id !== trackId),
        }));
      }
    } catch (err) {
      console.error("Review track error:", err);
    }
  };

  const handleRemoveTrack = async (trackId) => {
    try {
      await api.delete(`/playlists/${id}/tracks/${trackId}`);
      setPlaylist((prev) => ({ ...prev, tracks: prev.tracks.filter((t) => t.id !== trackId) }));
      if (activeTrack?.id === trackId) stopPlayback();
    } catch (err) {
      console.error("Remove track error:", err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cette playlist ?")) return;
    try {
      await api.delete(`/playlists/${id}`);
      navigate("/playlists");
    } catch (err) {
      console.error("Delete playlist error:", err);
    }
  }; return <div className="text-center py-12 text-muted-foreground">Chargement...</div>;
  if (!playlist) return null;

  const isCreator = playlist.is_creator;
  const canPropose = playlist.can_propose;
  const pendingCount = (playlist.pending_tracks || []).length;
  const playableTracks = getPlayableTracks();
  const pc = (p) => PLATFORM_COLORS[p] || { bg: "bg-foreground/5", border: "border-foreground/10", text: "text-muted-foreground", label: "" };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="playlist-detail">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate("/playlists")} className="mt-1 text-muted-foreground hover:text-[#FF6B35]" data-testid="playlist-back"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl font-bold text-foreground truncate" data-testid="playlist-detail-title">{playlist.title}</h1>
          {playlist.description && <p className="text-sm text-muted-foreground mt-0.5">{playlist.description}</p>}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-xs text-muted-foreground">{playlist.tracks?.length || 0} titres</span>
            {playlist.collaborative && <span className="text-xs text-[#FF6B35] flex items-center gap-0.5"><Users className="w-3 h-3" /> Collaborative</span>}
            <span className="text-xs text-muted-foreground">par {playlist.creator_pseudo}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {canPropose && (
            <button onClick={() => setShowAdd(true)} className="bg-[#FF6B35] text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 hover:scale-105 transition-transform" data-testid="add-track-btn">
              <Plus className="w-3.5 h-3.5" /> {isCreator ? "Ajouter" : "Proposer"}
            </button>
          )}
          {!canPropose && playlist.collaborative && !isCreator && (
            <div className="flex items-center gap-1 bg-muted text-muted-foreground px-3 py-1.5 rounded-full text-xs border border-border">
              <UserPlus className="w-3 h-3" /> Deviens ami pour proposer
            </div>
          )}
          {isCreator && (
            <button onClick={handleDelete} className="bg-red-500/10 text-red-400 px-3 py-1.5 rounded-full text-xs flex items-center gap-1 hover:bg-red-500/20 transition-colors" data-testid="delete-playlist-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Play All / Shuffle buttons */}
      {playableTracks.length > 0 && (
        <div className="flex gap-2" data-testid="playlist-controls">
          <button
            onClick={() => { setShuffle(false); playTrack(playableTracks[0]); }}
            className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B35] text-white py-2.5 rounded-full text-sm font-bold hover:bg-[#E85A25] transition-colors"
            data-testid="play-all-btn"
          >
            <Play className="w-4 h-4" fill="currentColor" /> Tout lire
          </button>
          <button
            onClick={() => {
              const newOrder = shuffleArray(playableTracks.map((_, i) => i));
              setShuffledOrder(newOrder);
              setShuffle(true);
              playTrack(playableTracks[newOrder[0]]);
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-foreground/5 border border-border text-foreground py-2.5 rounded-full text-sm font-bold hover:bg-foreground/10 transition-colors"
            data-testid="shuffle-play-btn"
          >
            <Shuffle className="w-4 h-4" /> Lecture aleatoire
          </button>
        </div>
      )}

      {/* Pending proposals badge for creator */}
      {isCreator && pendingCount > 0 && (
        <button onClick={() => setShowPending(!showPending)} className="w-full flex items-center justify-between bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-2xl px-4 py-3 text-sm font-medium text-[#FF6B35] hover:bg-[#FF6B35]/15 transition-colors" data-testid="pending-proposals-btn">
          <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{pendingCount} proposition{pendingCount > 1 ? "s" : ""} en attente</span>
          <span className="text-xs">{showPending ? "Masquer" : "Voir"}</span>
        </button>
      )}

      {/* Pending tracks */}
      <AnimatePresence>
        {showPending && isCreator && (playlist.pending_tracks || []).length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-2 overflow-hidden">
            {playlist.pending_tracks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl p-3" data-testid={`pending-track-${t.id}`}>
                <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.artist} - propose par {t.added_by_pseudo}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => handleReview(t.id, "approve")} className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 hover:bg-green-500/20 transition-colors" data-testid={`approve-${t.id}`}><Check className="w-4 h-4" /></button>
                  <button onClick={() => handleReview(t.id, "reject")} className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 hover:bg-red-500/20 transition-colors" data-testid={`reject-${t.id}`}><XCircle className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* NOW PLAYING - Embed player with controls */}
      <AnimatePresence>
        {activeTrack && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg" data-testid="embed-player">
            {/* Player controls bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-foreground/5">
              <button onClick={handlePrev} className="w-8 h-8 rounded-full bg-foreground/5 flex items-center justify-center text-foreground hover:bg-foreground/10 transition-colors" data-testid="player-prev">
                <SkipBack className="w-4 h-4" fill="currentColor" />
              </button>
              <button onClick={stopPlayback} className="w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center text-white" data-testid="player-pause">
                <Pause className="w-4 h-4" fill="currentColor" />
              </button>
              <button onClick={handleNext} className="w-8 h-8 rounded-full bg-foreground/5 flex items-center justify-center text-foreground hover:bg-foreground/10 transition-colors" data-testid="player-next">
                <SkipForward className="w-4 h-4" fill="currentColor" />
              </button>
              <button onClick={toggleShuffle} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${shuffleMode ? "bg-[#FF6B35]/20 text-[#FF6B35]" : "bg-foreground/5 text-muted-foreground hover:text-foreground"}`} data-testid="player-shuffle">
                <Shuffle className="w-3.5 h-3.5" />
              </button>
              <div className="flex-1 min-w-0 ml-1">
                <p className="text-xs font-semibold text-card-foreground truncate">{activeTrack.title}</p>
                <p className="text-[10px] text-muted-foreground truncate">{activeTrack.artist} <span className={pc(activeTrack.platform).text}>{pc(activeTrack.platform).label}</span></p>
              </div>
              <button onClick={stopPlayback} className="text-muted-foreground hover:text-red-400 p-1" data-testid="close-player"><X className="w-3.5 h-3.5" /></button>
            </div>
            {/* Embed iframe */}
            <iframe
              ref={iframeRef}
              key={activeTrack.id}
              src={activeTrack.embed_url}
              width="100%"
              height={200}
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              allowFullScreen
              loading="lazy"
              className="w-full"
              data-testid="embed-iframe"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add track modal */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowAdd(false)}>
            <motion.form initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={(e) => e.stopPropagation()} onSubmit={handleAdd} className="bg-card rounded-2xl p-6 w-96 max-w-[90vw] shadow-2xl space-y-3" data-testid="add-track-form">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-base text-card-foreground">{isCreator ? "Ajouter un titre" : "Proposer un titre"}</h3>
                <button type="button" onClick={() => setShowAdd(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-muted-foreground">Colle un lien YouTube pour ecouter le titre en entier</p>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre du morceau" className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none" data-testid="track-title-input" />
              <input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} placeholder="Artiste" className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none" data-testid="track-artist-input" />
              <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Lien YouTube (ex: youtube.com/watch?v=...)" className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none" data-testid="track-url-input" />
              <button type="submit" disabled={adding || !form.title.trim() || !form.url.trim()} className="w-full bg-[#FF6B35] text-white py-2.5 rounded-full text-sm font-bold hover:bg-[#E85A25] transition-colors disabled:opacity-50" data-testid="track-add-submit">
                {adding ? "..." : isCreator ? "Ajouter" : "Proposer"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tracks */}
      {(!playlist.tracks || playlist.tracks.length === 0) ? (
        <div className="text-center py-12">
          <Music2 className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">Aucun titre dans cette playlist</p>
          {canPropose && <p className="text-xs text-muted-foreground mt-1">Ajoute un morceau YouTube !</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {playlist.tracks.map((t, i) => {
            const p = pc(t.platform);
            const isCurrent = activeTrack?.id === t.id;
            return (
              <div key={t.id} className={`flex items-center gap-3 border rounded-xl p-3 transition-all ${isCurrent ? "border-[#FF6B35]/50 bg-[#FF6B35]/5 shadow-sm" : `${p.bg} ${p.border}`}`} data-testid={`track-item-${t.id}`}>
                {t.embed_url ? (
                  <button onClick={() => playTrack(t)} className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${isCurrent ? "bg-[#FF6B35] text-white animate-pulse" : "bg-foreground/10 text-muted-foreground hover:bg-[#FF6B35]/20 hover:text-[#FF6B35]"}`} data-testid={`play-${t.id}`}>
                    {isCurrent ? <Pause className="w-3.5 h-3.5" fill="currentColor" /> : <Play className="w-3.5 h-3.5 ml-0.5" fill={isCurrent ? "currentColor" : "none"} />}
                  </button>
                ) : (
                  <span className="text-muted-foreground text-xs font-mono w-8 text-center shrink-0">{i + 1}</span>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${isCurrent ? "text-[#FF6B35]" : "text-foreground"}`}>{t.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t.artist}
                    {p.label && <span className={`ml-1.5 ${p.text}`}>{p.label}</span>}
                  </p>
                  <p className="text-[10px] text-muted-foreground">par {t.added_by_pseudo}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {(t.added_by === user?.id || isCreator) && (
                    <button onClick={() => handleRemoveTrack(t.id)} className="w-7 h-7 rounded-full bg-foreground/5 flex items-center justify-center text-muted-foreground hover:text-red-400 transition-colors" data-testid={`remove-track-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
